import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  UserCircleIcon,
  ClockIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderType: 'employer' | 'student';
  recipientId: string;
  recipientName: string;
  recipientType: 'employer' | 'student';
  content: string;
  type: 'text' | 'file' | 'system';
  createdAt: Date | string | { _seconds: number; _nanoseconds: number };
  isRead: boolean;
}

interface Conversation {
  id: string;
  participants: {
    employer: {
      id: string;
      name: string;
      email: string;
    };
    student: {
      id: string;
      name: string;
      email: string;
    };
  };
  jobId?: string;
  applicationId?: string;
  jobTitle?: string;
  lastMessage?: {
    content: string;
    senderId: string;
    senderName: string;
    createdAt: Date | string | { _seconds: number; _nanoseconds: number };
  };
  lastActivity: Date | string | { _seconds: number; _nanoseconds: number };
  unreadCount: {
    employer: number;
    student: number;
  };
  status: 'active' | 'archived';
}

/**
 * Sample thread shown to signed-out visitors.
 *
 * The preview is the real chat UI, not a screenshot — visitors can read the
 * thread and type a reply. Sending is what requires an account, so the draft
 * is kept and the sign-in prompt appears at that point rather than the door.
 */
const PREVIEW_CONVERSATION: Conversation = {
  id: 'preview',
  participants: {
    employer: { id: 'preview-employer', name: 'Dana Okafor', email: 'dana@northwind.example' },
    student: { id: 'preview-student', name: 'You', email: '' },
  },
  jobTitle: 'Junior Frontend Developer',
  lastActivity: new Date(),
  unreadCount: { employer: 0, student: 0 },
  status: 'active',
};

const previewSentAt = (minutesAgo: number) => new Date(Date.now() - minutesAgo * 60_000);

const PREVIEW_MESSAGES: Message[] = [
  {
    id: 'preview-1',
    conversationId: 'preview',
    senderId: 'preview-employer',
    senderName: 'Dana Okafor',
    senderType: 'employer',
    recipientId: 'preview-student',
    recipientName: 'You',
    recipientType: 'student',
    content:
      "Hi! I saw your Frontend Fundamentals certification and the dashboard project on your profile — nice work on the accessibility pass.",
    type: 'text',
    createdAt: previewSentAt(52),
    isRead: true,
  },
  {
    id: 'preview-2',
    conversationId: 'preview',
    senderId: 'preview-employer',
    senderName: 'Dana Okafor',
    senderType: 'employer',
    recipientId: 'preview-student',
    recipientName: 'You',
    recipientType: 'student',
    content:
      "We're hiring a junior frontend dev and your assessment scores line up well. Would you be open to a short intro call this week?",
    type: 'text',
    createdAt: previewSentAt(51),
    isRead: true,
  },
  {
    id: 'preview-3',
    conversationId: 'preview',
    senderId: 'preview-student',
    senderName: 'You',
    senderType: 'student',
    recipientId: 'preview-employer',
    recipientName: 'Dana Okafor',
    recipientType: 'employer',
    content: "Thanks Dana — I'd love to. Thursday or Friday morning both work for me.",
    type: 'text',
    createdAt: previewSentAt(14),
    isRead: true,
  },
];

/** Where a preview draft is parked so signing in doesn't lose it. */
const PREVIEW_DRAFT_KEY = 'workazon:messagePreviewDraft';

const MessagesPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { userProfile, getToken, currentUser } = useAuth();
  // Signed-out visitors get a working preview of this page rather than a redirect.
  const isPreview = !currentUser;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessageText, setNewMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isPreview) {
      // Nothing to fetch — populate the sample thread and restore any draft the
      // visitor typed before being asked to sign in.
      setConversations([PREVIEW_CONVERSATION]);
      setSelectedConversation(PREVIEW_CONVERSATION);
      setMessages(PREVIEW_MESSAGES);
      setNewMessageText(sessionStorage.getItem(PREVIEW_DRAFT_KEY) || '');
      setLoading(false);
      return;
    }
    fetchConversations();
  }, [isPreview]);

  useEffect(() => {
    if (isPreview) return;
    if (conversationId) {
      fetchMessages(conversationId);
      // Find and set selected conversation
      const conversation = conversations.find(c => c.id === conversationId);
      if (conversation) {
        setSelectedConversation(conversation);
      }
    }
  }, [conversationId, conversations]);

  const formatDate = (timestamp: Date | string | { _seconds: number; _nanoseconds: number }) => {
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleString();
    } else if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
      return new Date(timestamp._seconds * 1000).toLocaleString();
    } else if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    return 'Unknown date';
  };

  const fetchConversations = async () => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/messages/conversations?userType=${userProfile?.userType || 'employer'}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setConversations(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch conversations');
      }
    } catch (error: any) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId: string) => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/messages/conversations/${convId}/messages`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setMessages(result.data.messages || []);
        if (result.data.conversation) {
          setSelectedConversation(result.data.conversation);
        }
      } else {
        throw new Error(result.message || 'Failed to fetch messages');
      }
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async () => {
    if (!newMessageText.trim() || !selectedConversation || sending) return;

    // Typing is free; delivering the message is what needs an account. Keep the
    // draft so it's still there on the other side of sign-up.
    if (isPreview) {
      sessionStorage.setItem(PREVIEW_DRAFT_KEY, newMessageText.trim());
      toast('Create a free account to send this message — your draft is saved.', { icon: '💬' });
      navigate('/register?next=/messages');
      return;
    }

    setSending(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/messages/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newMessageText.trim(),
          type: 'text'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setMessages(prev => [...prev, result.data]);
        setNewMessageText('');
        // Refresh conversations to update last message
        fetchConversations();
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {isPreview && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
            <p className="text-sm text-primary-900">
              <span className="font-semibold">This is a preview.</span>{' '}
              Have a look around and try writing a reply — you'll need a free account to send it.
            </p>
            <Link
              to="/register?next=/messages"
              className="shrink-0 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors text-center"
            >
              Create free account
            </Link>
          </div>
        )}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-8rem)]">
          <div className="flex h-full">
            {/* Conversations Sidebar */}
            <div className="w-1/3 border-r border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  conversations.map((conversation) => {
                    const isEmployer = userProfile?.userType === 'employer';
                    const otherParticipant = isEmployer ? conversation.participants.student : conversation.participants.employer;
                    const unreadCount = isEmployer ? conversation.unreadCount.employer : conversation.unreadCount.student;
                    
                    return (
                      <div
                        key={conversation.id}
                        onClick={() => {
                          setSelectedConversation(conversation);
                          fetchMessages(conversation.id);
                          window.history.pushState(null, '', `/messages/${conversation.id}`);
                        }}
                        className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                          selectedConversation?.id === conversation.id ? 'bg-indigo-50 border-indigo-200' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <UserCircleIcon className="h-10 w-10 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900">{otherParticipant.name}</p>
                              {conversation.jobTitle && (
                                <p className="text-sm text-gray-600 flex items-center gap-1">
                                  <BriefcaseIcon className="h-3 w-3" />
                                  {conversation.jobTitle}
                                </p>
                              )}
                              {conversation.lastMessage && (
                                <p className="text-sm text-gray-500 truncate max-w-48">
                                  {conversation.lastMessage.content}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            {conversation.lastMessage && (
                              <p className="text-xs text-gray-400">
                                {formatDate(conversation.lastMessage.createdAt)}
                              </p>
                            )}
                            {unreadCount > 0 && (
                              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                                {unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-3">
                      <UserCircleIcon className="h-8 w-8 text-gray-400" />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {userProfile?.userType === 'employer' 
                            ? selectedConversation.participants.student.name
                            : selectedConversation.participants.employer.name
                          }
                        </h3>
                        {selectedConversation.jobTitle && (
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <BriefcaseIcon className="h-3 w-3" />
                            {selectedConversation.jobTitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 mt-8">
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((message) => {
                        // In preview there's no signed-in uid to compare against,
                        // so the sample thread identifies its own side explicitly.
                        const isOwnMessage = isPreview
                          ? message.senderId === PREVIEW_CONVERSATION.participants.student.id
                          : message.senderId === userProfile?.uid;
                        
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                isOwnMessage
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-gray-200 text-gray-900'
                              }`}
                            >
                              <p className="text-sm">{message.content}</p>
                              <p className={`text-xs mt-1 ${
                                isOwnMessage ? 'text-indigo-200' : 'text-gray-500'
                              }`}>
                                {formatDate(message.createdAt)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200 bg-white">
                    <div className="flex gap-2">
                      {/* Enabled in preview too — typing is the whole point. */}
                      <textarea
                        value={newMessageText}
                        onChange={(e) => setNewMessageText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={
                          isPreview ? 'Type a reply to Dana…' : 'Type your message...'
                        }
                        rows={2}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!newMessageText.trim() || sending}
                        title={isPreview ? 'Create a free account to send' : 'Send message'}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </button>
                    </div>
                    {isPreview && newMessageText.trim() && (
                      <p className="mt-2 text-xs text-gray-500">
                        Press send — we'll keep this draft while you create your account.
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <UserCircleIcon className="h-16 w-16 mx-auto text-gray-300" />
                    <p className="mt-4">Select a conversation to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;